import React, { useEffect, useRef, useState } from "react";
import { CssBaseline, Box, Button } from "@mui/material";
import { gsap } from "gsap";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import { styled, Theme, useMediaQuery } from "@mui/system";
import Footer from "./FooterContainer";
import FloorPlans from "./FloorPlanNew";
import CallIcon from '@mui/icons-material/Call';
import LeadModal from "./LeadModal";
import { Helmet } from 'react-helmet-async';
import BottomNav from "./BottomNav";
import MasterLayout from "./MasterLayoutNew";
import HeroSectionMain from "./HeroSectionMain";
import ConnectedLiving from "./ConntedLivingNew";
import AboutUsNew from "./AboutUsNew";
import LuxuryFeatures from "./LuxuryFeatures";
import anime from "animejs/lib/anime.es.js";
import Lenis from "lenis";
import { kollalogowhite } from "../../../assets";
import Header from "./Header";
import SpecificationLightbox from "../../../Components/SpecificationLightbox";
import Cookies from "js-cookie"; // Import js-cookie for cookie management

gsap.registerPlugin(ScrollToPlugin);

const ButtonContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  right: "27px",
  top: "30%",
  transform: "translateY(-50%) rotate(-90deg)",
  transformOrigin: "right center",
  zIndex: 1000,


  [theme.breakpoints.down("sm")]: {
    right: "20px",
    top: "30%",
  },
}));


const AnimatedButton = styled(Button)({
  bgcolor: "#9f8054",
  color: "#fff",
  fontWeight: "bold",
  fontSize: "14px",
  letterSpacing:'1px',
  padding: "15px 20px",
  borderRadius: "10px 10px 0px 0px",
  boxShadow: "0px 4px 15px rgba(159, 128, 84, 0.5)",
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  zIndex: 100000,
  border: "1.5px solid #ffffff",
  transition: "transform 0.3s ease, box-shadow 0.3s",
  "&:hover": {
    bgcolor: "#7f6540",
    transform: "scale(1.05)",
    boxShadow: "0px 8px 25px rgba(159, 128, 84, 0.8)",
  },
  animation: "pulse 2s infinite", 
  "@keyframes pulse": {
    "0%": { boxShadow: "0 0 10px rgba(159, 128, 84, 0.5)" },
    "50%": { boxShadow: "0 0 25px rgba(159, 128, 84, 0.8)" },
    "100%": { boxShadow: "0 0 10px rgba(159, 128, 84, 0.5)" },
  },
});

const ProjectsPage: React.FC = () => {
  // ge the route from params and all and then switch the component based on luxor or luxuria... 
  const [index, setIndex] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [formFilled, setFormFilled] = useState<boolean>(false); // Track if the form is filled
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const sectionsRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    // Check if the form is already filled from cookies
    const isFormFilled = Cookies.get("formFilled") === "true";
    setFormFilled(isFormFilled);

    if (!isFormFilled) {
      // Open modal after 17 seconds
      const initialTimer = setTimeout(() => {
        if (!isOpen) {
          setIsOpen(true);
        }
      }, 25000);

      return () => clearTimeout(initialTimer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!formFilled) {
      // Reopen modal every 20 seconds after it is closed
      let reopenTimer: NodeJS.Timeout | null = null;

      if (!isOpen) {
        reopenTimer = setTimeout(() => {
          if (!isOpen) {
            setIsOpen(true);
          }
        }, 30000);
      }

      return () => {
        if (reopenTimer) clearTimeout(reopenTimer);
      };
    }
  }, [isOpen, formFilled]);



  useEffect(() => {
    // Initialize Lenis smooth scrolling
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
  }, []);

  useEffect(() => {
    gsap.to(window, { duration: 0, scrollTo: 0 });

    anime({
      targets: ".section h1",
      opacity: 0,
    });
  }, []);

  const handleScroll = () => {
    if (!sectionsRef.current) return;
    const sections = sectionsRef.current.children;
    let newIndex = 0;
    
    for (let i = 0; i < sections.length; i++) {
      const rect = sections[i].getBoundingClientRect();
      if (rect.top >= 0 && rect.top < window.innerHeight / 2) {
        newIndex = i;
        break;
      }
    }

    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex);
      
      anime({
        targets: `.section:nth-child(${newIndex + 1}) h1`,
        opacity: [0, 1],
        duration: 600,
        easing: "easeInOutQuad",
      });

      anime({
        targets: `.section:not(:nth-child(${newIndex + 1})) h1`,
        opacity: [1, 0],
        duration: 400,
        easing: "easeInOutQuad",
      });
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeIndex]);

  return (
    <>
    <div ref={sectionsRef}>

    <Helmet>
      <title>3 BHK Flats for Sale in Hyderabad | Kolla Group</title>

      <meta name="description" content="Premium 3 BHK flats for sale in Chanda Nagar, Hyderabad. Excellent connectivity to IT hubs & metro. Schedule your site visit with Kolla Group."  />

      <meta name="keywords" content="3 BHK flats in Chanda Nagar, 3 BHK flats for sale in Chanda Nagar, 3 BHK flats in Hyderabad, flats for sale in Chanda Nagar Hyderabad, gated community flats in Chanda Nagar, apartments near Miyapur Metro, premium flats in Chanda Nagar, residential projects in Chanda Nagar, ready to move 3 BHK flats Hyderabad, Kolla Group flats"  />

      <meta name="author" content="Kolla Group" />
      <meta property="og:title" content="3 BHK Flats for Sale in Hyderabad | Kolla Group" />
      <meta property="og:description" content="Premium 3 BHK flats for sale in Chanda Nagar, Hyderabad. Excellent connectivity to IT hubs & metro. Schedule your site visit with Kolla Group." />
      <meta property="og:image" content="https://kollagroup.com/kollaluxor.png" />
      <meta property="og:url" content="https://kollagroup.com/luxor-exclusive" />
      <meta property="og:type" content="website" />

      <meta name="twitter:card" content="https://kollagroup.com/kollaluxor.png" />
      <meta name="twitter:title" content="3 BHK Flats for Sale in Hyderabad | Kolla Group" />
      <meta name="twitter:description" content="Premium 3 BHK flats for sale in Chanda Nagar, Hyderabad. Excellent connectivity to IT hubs & metro. Schedule your site visit with Kolla Group." />
      <meta name="twitter:image" content="https://kollagroup.com/kollaluxor.png" />

      <link rel="canonical" href="https://kollagroup.com/luxor-exclusive" />

      <meta name="robots" content="index, follow" />

      <meta name="viewport" content="width=device-width, initial-scale=1" />

      <link rel="icon" href="https://www.kollagroup.com/favicon.ico" />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Residence",
          "name": "Luxor Exclusive",
          "url": "https://kollagroup.com/luxor-exclusive",
          "description": "2.5 & 3 BHK luxury apartments in Hyderabad by Kolla Group",
          "image": kollalogowhite,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Chandanagar",
            "addressRegion": "Telangana",
            "addressCountry": "India"
          },        
          "publisher": {
            "@type": "Organization",
            "name": "Kolla Group",
            "logo": kollalogowhite
          }
        })}
      </script>
    </Helmet>

    <div style={{ display: "none"}}>
      <h1>2.5 & 3 BHK Luxury Apartments in Hyderabad</h1>
      <p>
        Luxury villas in hyderabad, high rise apartments hyderabad, Luxury villas in hyderabad gated communities, best apartments in hyderabad, best villas in hyderabad, ultra luxury apartments in hyderabad, premium apartments in hyderabad, ultra luxury villas in hyderabad, top 10 luxury apartments in hyderabad, top apartments in hyderabad, luxury house in hyderabad, luxury homes in hyderabad, best villa projects in hyderabad
      </p>
      <a href="https://kollagroup.com/luxor-exclusive">Luxor</a>
      <a href="https://kollagroup.com/luxuria">Luxuria</a>
      <a href="https://kollagroup.com/contactus">Contact</a>
      <a href="https://kollagroup.com/">Home</a>
      <a href="https://www.instagram.com/kolla_group/">Instagram</a>
      <a href="https://www.facebook.com/kollahousing">Facebook</a>
      <a href="https://www.youtube.com/@KOLLA_Group">YouTube</a>
      <a href="https://www.linkedin.com/company/kolla_group/">LinkedIn</a>
    </div>

        <Header />

        <Box sx={{display: "none"}}>
          <h1>2.5 & 3 BHK Luxury Apartments in Hyderabad</h1>
        </Box>

        <Box component="section" id="Home" className="section" sx={{ minHeight: isMobile ? "auto" : "100vh" }}>
          <HeroSectionMain />
        </Box>
        

        <Box component="section" id="AboutUs" className="section" sx={{ minHeight: isMobile ? "auto" : "100vh" }}>
          <AboutUsNew />
        </Box>
        
        <Box component="section" id="Layout" className="section" sx={{ minHeight: isMobile ? "auto" : "100vh" }}>
          <MasterLayout />
        </Box>



        <Box component="section" id="FloorPlans" className="section" sx={{ maxHeight: isMobile ? "auto" : "100vh" }}>
          <FloorPlans />
        </Box>



        <Box component="section" id="Location" className="section" sx={{ minHeight: "auto" }}>  
          <ConnectedLiving />
        </Box>



        {isMobile && 
        <Box component="section" id="Gallery" className="section" sx={{ minHeight: "auto" }}>
          <LuxuryFeatures />
        </Box>
        }

        <Box component="section" id="Gallery" className="section" sx={{ minHeight: "auto" }}>
          <SpecificationLightbox />
        </Box>


        
        <Box component="section" id="Contact" className="section" sx={{ minHeight: "auto" }}>
          <Footer />
        </Box>

         {!isMobile ? 
          <ButtonContainer>
            <AnimatedButton
              ref={buttonRef}
              variant="contained"
              onClick={() => setIsOpen(true)}
            >
              <CallIcon />
              Contact Us
            </AnimatedButton>
          </ButtonContainer>
          :  <BottomNav /> }




          <LeadModal isOpen={isOpen} onClose={() => setIsOpen(false)} />

        </div>

    </>
  );
};

export default ProjectsPage;
